import { Injectable, signal } from '@angular/core'
import { User, UserRole } from '../models/models'
import { CHANGELOG, ChangelogRelease } from './changelog'
import { semverGt } from './semver.util'
import { APP_VERSION } from '../version/app-version'

/** Which "What's New" dialog (if any) is currently shown. */
export type WhatsNewMode = 'hidden' | 'auto' | 'full'

/**
 * WhatsNewService — owns the "What's New" changelog dialog state.
 *
 * Two entry points:
 *   • auto  — shown once per new version, on first sign-in after an upgrade,
 *             filtered to releases newer than the last version the user saw.
 *   • full  — shown on demand from the user menu; lists every release relevant
 *             to the user's roles regardless of what they've seen.
 *
 * "Last seen" is persisted in localStorage so the auto prompt fires at most
 * once per deployed version per browser.
 */
@Injectable({ providedIn: 'root' })
export class WhatsNewService {
  private static readonly LAST_SEEN_KEY = 'whatsNew.lastSeenVersion'

  /** Current dialog mode; the AppComponent renders the dialog off this. */
  readonly dialogMode = signal<WhatsNewMode>('hidden')

  /**
   * Returns the releases worth showing to a user with `roles`:
   *   • entries are filtered to those whose `roles` intersect the user's,
   *   • releases left with no matching entries are dropped, and
   *   • when `sinceVersion` is given, only releases strictly newer than it are
   *     kept (used by the auto prompt to show "what changed since last time").
   */
  relevantReleases(roles: UserRole[], sinceVersion?: string): ChangelogRelease[] {
    return CHANGELOG
      .filter(release => sinceVersion === undefined || semverGt(release.version, sinceVersion))
      .map(release => ({
        ...release,
        entries: release.entries.filter(entry =>
          entry.roles.some(role => roles.includes(role))),
      }))
      .filter(release => release.entries.length > 0)
  }

  /**
   * Auto-shows the changelog once, on first sign-in after an upgrade. Skipped
   * when the running version was already seen, or when there's nothing relevant
   * to this user's roles.
   */
  maybeAutoShow(user: User): void {
    const lastSeen = this.lastSeenVersion()
    if (APP_VERSION === lastSeen) return

    if (this.relevantReleases(user.roles, lastSeen).length > 0) {
      this.dialogMode.set('auto')
    }
  }

  /** Opens the full changelog on demand (from the user menu). */
  openFull(): void {
    this.dialogMode.set('full')
  }

  /**
   * Closes the dialog and records the running version as seen, so the auto
   * prompt won't fire again until the next deployed upgrade.
   */
  dismiss(): void {
    this.writeLastSeen(APP_VERSION)
    this.dialogMode.set('hidden')
  }

  /**
   * The last version the user dismissed the changelog at; '' if never.
   * Read fresh from localStorage (only mutated on `dismiss()`), so it stays a
   * stable "since" reference for the auto dialog until it's closed.
   */
  lastSeenVersion(): string {
    try {
      return localStorage.getItem(WhatsNewService.LAST_SEEN_KEY) ?? ''
    } catch {
      return ''
    }
  }

  /** Persists the last-seen version; silently no-ops if storage is unavailable. */
  private writeLastSeen(version: string): void {
    try {
      localStorage.setItem(WhatsNewService.LAST_SEEN_KEY, version)
    } catch {
      // Ignore — private-mode / disabled storage just loses the "seen" memory.
    }
  }
}
